import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getIndustries, getJobLevels } from "../api";

/**
 * Job-search filters (salary / level / industry / employment type) for the
 * /search page's Ажил tab.
 *
 * Lives above both Header (which renders the filter icon + dropdown, next to
 * the notification bell) and the routed page (Search.jsx, which only reads
 * the resulting apiFilters) — the two are siblings under Layout, so this is
 * the one place they can actually share state. See Home.jsx for the original,
 * per-page version of the same four filters this mirrors.
 */
const SearchFilterContext = createContext(null);

const SALARY_MIN_LIMIT = 0;
const SALARY_MAX_LIMIT = 10000000;
const SALARY_STEP = 100000;

export const SearchFilterProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [salaryRange, setSalaryRange] = useState([SALARY_MIN_LIMIT, SALARY_MAX_LIMIT]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedEmploymentType, setSelectedEmploymentType] = useState("");

  const [jobLevels, setJobLevels] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [referenceLoading, setReferenceLoading] = useState(false);
  const fetchedRef = useRef(false);

  // Reference lists (job levels, industries) cost a request each — fetched once, the
  // first time anyone actually opens the panel, not on every page that mounts this
  // provider.
  const ensureReferenceData = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setReferenceLoading(true);
    const [industriesResponse, jobLevelsResponse] = await Promise.all([
      getIndustries(),
      getJobLevels(),
    ]);
    if (industriesResponse.success && industriesResponse.data) setIndustries(industriesResponse.data);
    if (jobLevelsResponse.success && jobLevelsResponse.data) setJobLevels(jobLevelsResponse.data);
    setReferenceLoading(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next) ensureReferenceData();
      return next;
    });
  }, [ensureReferenceData]);
  const close = useCallback(() => setIsOpen(false), []);

  const toggleLevel = useCallback((id) => {
    setSelectedLevels((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  }, []);

  const toggleIndustry = useCallback((id) => {
    setSelectedIndustries((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  }, []);

  const reset = useCallback(() => {
    setSalaryRange([SALARY_MIN_LIMIT, SALARY_MAX_LIMIT]);
    setSelectedLevels([]);
    setSelectedIndustries([]);
    setSelectedEmploymentType("");
  }, []);

  const getLevelDisplayName = useCallback((level) => {
    const lang = i18n.language;
    if (lang === 'mn') return level.name || level.ruName || level.enName;
    if (lang === 'kz') return level.kzName || level.ruName || level.name;
    if (lang === 'uz') return level.uzName || level.kzName || level.ruName || level.name;
    return level.ruName || level.name || level.enName;
  }, [i18n.language]);

  const getIndustryDisplayName = useCallback((industry) => {
    const lang = i18n.language;
    if (lang === 'mn') return industry.name || industry.ruName;
    if (lang === 'kz') return industry.kzName || industry.ruName || industry.name;
    if (lang === 'uz') return industry.uzName || industry.kzName || industry.ruName || industry.name;
    return industry.ruName || industry.name;
  }, [i18n.language]);

  const salaryFilterActive = salaryRange[0] > SALARY_MIN_LIMIT || salaryRange[1] < SALARY_MAX_LIMIT;

  const activeCount = useMemo(() => {
    let count = 0;
    if (salaryFilterActive) count += 1;
    if (selectedLevels.length > 0) count += 1;
    if (selectedIndustries.length > 0) count += 1;
    if (selectedEmploymentType) count += 1;
    return count;
  }, [salaryFilterActive, selectedLevels, selectedIndustries, selectedEmploymentType]);

  // Ready to spread straight into a job-search request, same field names Home.jsx sends.
  const apiFilters = useMemo(() => {
    const filters = {};
    if (salaryRange[0] > SALARY_MIN_LIMIT) filters.minSalary = salaryRange[0];
    if (salaryRange[1] < SALARY_MAX_LIMIT) filters.maxSalary = salaryRange[1];
    if (selectedLevels.length > 0) filters.jobLevelIds = selectedLevels;
    if (selectedIndustries.length > 0) filters.industryIds = selectedIndustries;
    if (selectedEmploymentType) filters.employmentType = selectedEmploymentType;
    return filters;
  }, [salaryRange, selectedLevels, selectedIndustries, selectedEmploymentType]);

  const value = useMemo(() => ({
    isOpen, toggle, close,
    salaryRange, setSalaryRange, salaryMinLimit: SALARY_MIN_LIMIT, salaryMaxLimit: SALARY_MAX_LIMIT, salaryStep: SALARY_STEP,
    salaryFilterActive,
    selectedLevels, toggleLevel,
    selectedIndustries, toggleIndustry,
    selectedEmploymentType, setSelectedEmploymentType,
    jobLevels, industries, referenceLoading,
    getLevelDisplayName, getIndustryDisplayName,
    activeCount, reset, apiFilters,
  }), [
    isOpen, toggle, close,
    salaryRange, salaryFilterActive,
    selectedLevels, toggleLevel,
    selectedIndustries, toggleIndustry,
    selectedEmploymentType,
    jobLevels, industries, referenceLoading,
    getLevelDisplayName, getIndustryDisplayName,
    activeCount, reset, apiFilters,
  ]);

  return (
    <SearchFilterContext.Provider value={value}>
      {children}
    </SearchFilterContext.Provider>
  );
};

export const useSearchFilter = () => {
  const ctx = useContext(SearchFilterContext);
  if (!ctx) throw new Error("useSearchFilter must be used within a SearchFilterProvider");
  return ctx;
};
